import * as React from "react";
import { useCallback, useContext, useMemo, useState } from "react";
import { useField } from "formik";
import styled from "styled-components";
import { SortableList, FormikInput, InputDescription, Link } from "components/common";
import IconButton from "components/common/IconButton";
import NoEntitiesExist from "components/common/NoEntitiesExist";
import defaultTitle from "views/components/defaultTitle";
import WidgetDetailsContext from "report/report-creation/WidgetDetailsContext";
import { Button } from "components/bootstrap";
import WidgetTitle from "report/common/WidgetTitle";
import Routes from "routing/Routes";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
const WidgetListItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;
const WidgetInfo = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: top;
`;
const WidgetTitleWrapper = styled.div`
  margin-top: 1px;
`;
const WidgetActions = styled.div`
  display: flex;
`;
const WidgetDescription = ({ onRemove, id, dashboardId, index, disabled }) => {
  var _a;
  const { widgets, isLoading } = useContext(WidgetDetailsContext);
  const { permissions } = useCurrentUser();
  const [, { error }] = useField(`widgets.${index}`);
  const widgetDetails = useMemo(() => widgets[id], [widgets, id]);
  const [showDetailsConfiguration, setShowDetailsConfiguration] = useState(false);
  const _onRemove = useCallback(() => {
    if (window.confirm("Are you sure you want to delete this widget?")) {
      onRemove(id, dashboardId);
    }
  }, [dashboardId, id, onRemove]);
  const toggleEditModal = () => {
    setShowDetailsConfiguration((cur) => !cur);
  };
  const [, { value: customTitle }] = useField(`widgets.${index}.title`);
  const detailsAreAvailable = !isLoading && !!widgetDetails;
  const widgetTitle = (_a = widgetDetails == null ? void 0 : widgetDetails.description) != null ? _a : widgetDetails ? defaultTitle(widgetDetails) : "";
  const title = !(customTitle == null ? void 0 : customTitle.trim()) ? widgetTitle : customTitle;
  const isEditPermitted = isPermitted(permissions, `view:edit:${dashboardId}`);
  return /* @__PURE__ */ React.createElement(WidgetListItem, null, /* @__PURE__ */ React.createElement(WidgetInfo, null, /* @__PURE__ */ React.createElement(WidgetTitleWrapper, null, /* @__PURE__ */ React.createElement(
    WidgetTitle,
    {
      title,
      isLoading: isLoading && !widgetDetails,
      detailsAreAvailable
    }
  )), /* @__PURE__ */ React.createElement(WidgetActions, null, /* @__PURE__ */ React.createElement(
    IconButton,
    {
      title: "Edit widget description",
      name: "edit_square",
      onClick: toggleEditModal,
      disabled: !detailsAreAvailable || disabled
    }
  ), isEditPermitted && /* @__PURE__ */ React.createElement(Link, { to: `${Routes.dashboard_show(dashboardId)}?focusedId=${id}&editing=true`, target: "_blank" }, /* @__PURE__ */ React.createElement(IconButton, { name: "checkbook", title: "Edit widget on the dashboard" })), /* @__PURE__ */ React.createElement(IconButton, { title: "Remove this widget from report", name: "delete", onClick: _onRemove, disabled }))), error && /* @__PURE__ */ React.createElement(InputDescription, { error, className: "no-bm" }), showDetailsConfiguration && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      name: `widgets.${index}.title`,
      id: `widgets.${index}.title`,
      label: "Custom Title",
      placeholder: widgetTitle
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      name: `widgets.${index}.description`,
      id: `widgets.${index}.description`,
      label: "Custom Description",
      type: "textarea"
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      id: `widgets.${index}.height`,
      label: "Custom widget height",
      name: `widgets.${index}.height`,
      help: "Relative value from 1 to 12",
      min: 1,
      max: 12
    }
  ), /* @__PURE__ */ React.createElement(Button, { onClick: toggleEditModal, bsSize: "small" }, "Hide")));
};
const WidgetsList = ({ disabled }) => {
  const [{ value: widgets }, , { setValue: setWidgets }] = useField("widgets");
  const [, , { setValue: setPositions }] = useField("positions");
  const onRemove = useCallback(
    (widgetId, dashboardId) => setWidgets(widgets.filter((widget) => widget.widgetId !== widgetId || widget.dashboardId !== dashboardId)),
    [setWidgets, widgets]
  );
  const onMoveItem = useCallback(
    (newWidgetItems) => {
      const newWidgets = newWidgetItems.map((widgetItem) => {
        const [dashboardId, widgetId] = widgetItem.id.split("/");
        return widgets.find((widget) => widget.widgetId === widgetId && widget.dashboardId === dashboardId);
      });
      const newPositions = newWidgets.map((widget, idx) => ({
        row: idx + 1,
        col: 1,
        dashboard_widget_id: widget.widgetId
      }));
      setPositions(newPositions);
      return setWidgets(newWidgets);
    },
    [setPositions, setWidgets, widgets]
  );
  const listItems = useMemo(
    () => widgets.map(({ widgetId, dashboardId }, index) => ({
      id: `${dashboardId}/${widgetId}`,
      title: /* @__PURE__ */ React.createElement(
        WidgetDescription,
        {
          id: widgetId,
          index,
          dashboardId,
          onRemove,
          disabled
        }
      )
    })),
    [onRemove, widgets, disabled]
  );
  return listItems.length > 0 ? /* @__PURE__ */ React.createElement(SortableList, { items: listItems, onMoveItem, disableDragging: disabled }) : /* @__PURE__ */ React.createElement(NoEntitiesExist, null, "This report has no widgets yet.");
};
export default WidgetsList;
